import { useObserve } from "@legendapp/state/react";
import { useMatch } from "@tanstack/react-router";
import { useEffect } from "react";

import { boards$ } from "~/stores/boards";
import { datasets$ } from "~/stores/datasets";
import { threads$ } from "~/stores/threads";

const DEFAULT_TITLE = "Mora";

/** Reactively updates `document.title` based on the current route. */
export const DocumentTitle = () => {
  const boardMatch = useMatch({
    from: "/boards/$boardId",
    shouldThrow: false,
  });
  const datasetMatch = useMatch({
    from: "/_app/datasets/$datasetId",
    shouldThrow: false,
  });
  const threadMatch = useMatch({
    from: "/_app/thread/$threadId",
    shouldThrow: false,
  });

  if (boardMatch && boardMatch.status !== "notFound") {
    return <BoardDocumentTitle boardId={boardMatch.params.boardId} />;
  }
  if (datasetMatch)
    return <DatasetDocumentTitle datasetId={datasetMatch.params.datasetId} />;
  if (threadMatch)
    return <ThreadDocumentTitle threadId={threadMatch.params.threadId} />;

  return <DefaultDocumentTitle />;
};

const BoardDocumentTitle = ({ boardId }: { boardId: string }) => {
  useObserve(() => {
    const name = boards$[boardId].name.get();
    queueMicrotask(() => {
      document.title = `${name || "Untitled board"} | Mora`;
    });
  });
  return null;
};

const DatasetDocumentTitle = ({ datasetId }: { datasetId: string }) => {
  useObserve(() => {
    const name = datasets$[datasetId].name.get();
    queueMicrotask(() => {
      document.title = `${name || "Untitled dataset"} | Mora`;
    });
  });
  return null;
};

const ThreadDocumentTitle = ({ threadId }: { threadId: string }) => {
  useObserve(() => {
    const title = threads$[threadId].title.get();
    queueMicrotask(() => {
      document.title = `${title || "New thread"} | Mora`;
    });
  });
  return null;
};

const DefaultDocumentTitle = () => {
  useEffect(() => {
    document.title = DEFAULT_TITLE;
  }, []);
  return null;
};
